'use client';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { useCallbackRef } from '@radix-ui/react-use-callback-ref';
import { forwardRef, useState, useRef, useEffect, useCallback } from 'react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { Slot } from '@radix-ui/react-slot';
import { getTabbableForFirstFocus, removeLinks, getTabbableCandidates, focus, focusFirst, getTabbableEdges, arrayRemove } from './helpers.mjs';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

var AUTOFOCUS_ON_MOUNT = "focusScope.autoFocusOnMount";
var AUTOFOCUS_ON_UNMOUNT = "focusScope.autoFocusOnUnmount";
var EVENT_OPTIONS = { bubbles: false, cancelable: true };
var FocusScope = forwardRef(
  ({
    loop = false,
    trapped = true,
    trappedContent = false,
    onMountAutoFocus: onMountAutoFocusProp,
    onUnmountAutoFocus: onUnmountAutoFocusProp,
    ...props
  }, forwardedRef) => {
    const [container, setContainer] = useState(
      null
    );
    const onMountAutoFocus = useCallbackRef(onMountAutoFocusProp);
    const onUnmountAutoFocus = useCallbackRef(onUnmountAutoFocusProp);
    const lastFocusedElementRef = useRef(null);
    const composedRefs = useComposedRefs(
      forwardedRef,
      (node) => setContainer(node)
    );
    const focusScope = useRef({
      paused: false,
      pause() {
        this.paused = true;
      },
      resume() {
        this.paused = false;
      }
    }).current;
    useEffect(() => {
      if (trapped) {
        const handleFocusIn = (event) => {
          if (focusScope.paused || !container)
            return;
          const target = event.target;
          if (container.contains(target)) {
            lastFocusedElementRef.current = target;
          } else {
            focus(lastFocusedElementRef.current, { select: true });
          }
        };
        const handleFocusOut = (event) => {
          if (focusScope.paused || !container)
            return;
          const relatedTarget = event.relatedTarget;
          if (relatedTarget === null)
            return;
          if (!container.contains(relatedTarget)) {
            focus(lastFocusedElementRef.current, { select: true });
          }
        };
        const handleMutations = (mutations) => {
          const focusedElement = document.activeElement;
          if (focusedElement !== document.body)
            return;
          for (const mutation of mutations) {
            if (mutation.removedNodes.length > 0)
              focus(container);
          }
        };
        document.addEventListener("focusin", handleFocusIn);
        document.addEventListener("focusout", handleFocusOut);
        const mutationObserver = new MutationObserver(handleMutations);
        if (container)
          mutationObserver.observe(container, {
            childList: true,
            subtree: true
          });
        return () => {
          document.removeEventListener("focusin", handleFocusIn);
          document.removeEventListener("focusout", handleFocusOut);
          mutationObserver.disconnect();
        };
      }
    }, [trapped, container, focusScope.paused]);
    useEffect(() => {
      if (container) {
        focusScopesStack.add(focusScope);
        const previouslyFocusedElement = document.activeElement;
        const hasFocusedCandidate = container.contains(
          previouslyFocusedElement
        );
        if (!hasFocusedCandidate) {
          const mountEvent = new CustomEvent(AUTOFOCUS_ON_MOUNT, EVENT_OPTIONS);
          container.addEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);
          container.dispatchEvent(mountEvent);
          if (!mountEvent.defaultPrevented) {
            const tabbableElements = getTabbableForFirstFocus(
              removeLinks(getTabbableCandidates(container))
            );
            if (tabbableElements.length === 0) {
              focus(container);
            } else {
              if (trappedContent) {
                focusFirst(tabbableElements, {
                  select: true
                });
              } else {
                focus(container);
              }
            }
          }
        }
        return () => {
          container.removeEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);
          setTimeout(() => {
            const unmountEvent = new CustomEvent(
              AUTOFOCUS_ON_UNMOUNT,
              EVENT_OPTIONS
            );
            container.addEventListener(
              AUTOFOCUS_ON_UNMOUNT,
              onUnmountAutoFocus
            );
            container.dispatchEvent(unmountEvent);
            if (!unmountEvent.defaultPrevented) {
              focus(previouslyFocusedElement ?? document.body, {
                select: true
              });
            }
            container.removeEventListener(
              AUTOFOCUS_ON_UNMOUNT,
              onUnmountAutoFocus
            );
            focusScopesStack.remove(focusScope);
          }, 0);
        };
      }
    }, [
      container,
      onMountAutoFocus,
      onUnmountAutoFocus,
      focusScope,
      trappedContent
    ]);
    const handleKeyDown = useCallback(
      (event) => {
        if (!loop && !trapped)
          return;
        if (focusScope.paused)
          return;
        const isTabKey = event.key === "Tab" && !event.altKey && !event.ctrlKey && !event.metaKey;
        const focusedElement = document.activeElement;
        if (isTabKey && focusedElement) {
          const currentTarget = event.currentTarget;
          const [first, last] = getTabbableEdges(currentTarget);
          const hasTabbableElementsInside = first && last;
          if (!hasTabbableElementsInside) {
            if (focusedElement === currentTarget)
              event.preventDefault();
          } else {
            if (!event.shiftKey && focusedElement === last) {
              event.preventDefault();
              if (loop)
                focus(first, { select: true });
            } else if (event.shiftKey && focusedElement === first) {
              event.preventDefault();
              if (loop)
                focus(last, { select: true });
            }
          }
        }
      },
      [loop, trapped, focusScope.paused]
    );
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        Slot,
        {
          tabIndex: -1,
          ref: composedRefs,
          ...props,
          onKeyDown: composeEventHandlers(props.onKeyDown, handleKeyDown)
        }
      ),
      /* @__PURE__ */ jsx(
        "span",
        {
          "wds-component": "focus-guard",
          tabIndex: trapped ? 0 : -1,
          style: {
            outline: "none",
            opacity: "0",
            position: "fixed",
            pointerEvents: "none"
          }
        }
      )
    ] });
  }
);
FocusScope.displayName = "FocusScope";
var createFocusScopesStack = () => {
  let stack = [];
  return {
    add(focusScope) {
      const activeFocusScope = stack[0];
      if (focusScope !== activeFocusScope) {
        activeFocusScope?.pause();
      }
      stack = arrayRemove(stack, focusScope);
      stack.unshift(focusScope);
    },
    remove(focusScope) {
      stack = arrayRemove(stack, focusScope);
      stack[0]?.resume();
    }
  };
};
var focusScopesStack = createFocusScopesStack();
var focus_scope_default = FocusScope;

export { focus_scope_default as default };
